import React, { useCallback, useEffect, useMemo } from 'react';
import { useToast } from '../../components/common/Toast';
import { useAuthStore } from '../../store/authStore';
import { translate } from '../../i18n/translate';
import { LanguageContext } from './LanguageContext';
import { useLanguagePreferences } from './preferences/useLanguagePreferences';

interface LanguageProviderProps {
  children: React.ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  const { user } = useAuthStore();
  const { showToast } = useToast();
  const handleSyncError = useCallback(() => {
    showToast(translate('en', 'dashboard.syncLanguageWarning'), 'warning');
  }, [showToast]);
  const languageState = useLanguagePreferences({
    userId: user?.id,
    onSyncError: handleSyncError,
  });
  const { retryPendingSync, languageCode } = languageState;

  useEffect(() => {
    if (!user?.id) {
      return;
    }
    retryPendingSync();
  }, [retryPendingSync, user?.id]);

  useEffect(() => {
    const handleOnline = () => {
      retryPendingSync();
    };
    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [retryPendingSync]);

  const t = useCallback(
    (key: string, params?: Record<string, string | number>, fallback?: string) =>
      translate(languageCode, key, params, fallback),
    [languageCode]
  );

  const contextValue = useMemo(
    () => ({
      ...languageState,
      t,
    }),
    [languageState, t]
  );

  return <LanguageContext.Provider value={contextValue}>{children}</LanguageContext.Provider>;
};
